import { User } from 'oidc-client-ts';
import { useCallback, useEffect, useState } from 'react';

import { AuthService } from 'shared/config/oidcClient/authServices';
import { USER_ACCESS_TOKEN } from 'shared/constants/localstorage';

type UserData = User['profile'] & { roles: string[] };

function useProvideAuth() {
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isInited, setIsInited] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);

  const logout = useCallback(async () => {
    const authService = new AuthService();
    localStorage.removeItem(USER_ACCESS_TOKEN);
    setUserData(null);
    setUserRoles([]);
    setAuthenticated(false);
    await authService.logout();
  }, []);

  const login = useCallback(async (user: User) => {
    const accessTokenParts = user.access_token?.split('.') || [];
    const isJwtAccessToken = accessTokenParts.length === 3;
    const tokenToUse = isJwtAccessToken ? user.access_token : user.id_token;

    if (tokenToUse) {
      localStorage.setItem(USER_ACCESS_TOKEN, tokenToUse);
    } else {
      console.error('No valid token available from OIDC user');
      localStorage.removeItem(USER_ACCESS_TOKEN);
      return;
    }

    const rolesClaim = user?.profile?.['urn:zitadel:iam:org:project:roles'];
    const roles: string[] =
      rolesClaim &&
      typeof rolesClaim === 'object' &&
      rolesClaim !== null &&
      !Array.isArray(rolesClaim)
        ? Object.keys(rolesClaim)
        : [];

    setUserData({ ...user?.profile, roles: roles });
    setUserRoles(roles);
    setAuthenticated(true);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authService = new AuthService();
        const user = await authService.getUser();
        if (user && !user.expired) {
          await login(user);
        } else {
          localStorage.removeItem(USER_ACCESS_TOKEN);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem(USER_ACCESS_TOKEN);
      } finally {
        setIsLoading(false);
        setIsInited(true);
      }
    };

    initAuth();
  }, [login]);

  return {
    authenticated,
    setAuthenticated,
    userData,
    setUserData,
    logout,
    login,
    isLoading,
    setIsLoading,
    userRoles,
    setUserRoles,
    isInited,
    setIsInited,
  };
}

export default useProvideAuth;
